import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from "react";

// Per-device "use fixed sidebar" preference. When enabled, the School layout
// detaches the nav menu from the hamburger drawer and renders it as a permanent
// left sidebar.
//
// The choice lives in localStorage and nowhere else. It is a property of the
// screen, not of the person: the sidebar takes real estate away from the page
// beside it, so the same staff member wants it on a desk monitor and not on a
// laptop. Storing it per device also means the inline script in the application
// layout can decide the legacy layout before first paint with no round trip and
// no flash.
const STORAGE_KEY = "use_fixed_sidebar";

// Width of the permanent left sidebar the School layout renders when the
// preference is on. Shared so fixed-position chrome (e.g. the quick-actions
// FAB) can offset itself to stay aligned with the content area.
export const FIXED_SIDEBAR_WIDTH = 272;

// The floor the legacy pages hold themselves to, mirrored from the wrapper in
// the v1 MainLayout (the two bundles are separate, so it cannot be imported).
// A legacy page never lays out narrower than this; a narrower window scrolls
// horizontally instead.
const LEGACY_PAGE_MIN_WIDTH = 1400;

// Narrowest viewport the mode is offered on. The sidebar reserves its width
// beside the page instead of overlaying it, so anything under the sum of the
// two leaves a legacy page less room than it can lay out in and pushes it into
// horizontal scrolling - and since the sidebar is fixed to the viewport, the
// page would then slide underneath it. Under this width the option is not
// offered at all, and a preference stored on a wider screen stays inert until
// the width comes back.
export const FIXED_SIDEBAR_MIN_VIEWPORT =
  FIXED_SIDEBAR_WIDTH + LEGACY_PAGE_MIN_WIDTH;
export const FIXED_SIDEBAR_MEDIA_QUERY = `(min-width: ${FIXED_SIDEBAR_MIN_VIEWPORT}px)`;

// Read the stored preference. Exported for the legacy chrome entrypoint, which
// needs it outside React to decide what to mount over the plain v1 document.
export function readFixedSidebarPreference() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // localStorage can throw in private mode / blocked storage; default off.
    return false;
  }
}

function writeFixedSidebarPreference(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

// Live viewport check, subscribed rather than sampled so shrinking the window
// past the threshold withdraws the option (and the sidebar) instead of leaving
// a clipped page behind.
function subscribeToWidth(onChange) {
  const query = window.matchMedia(FIXED_SIDEBAR_MEDIA_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function readWideEnough() {
  try {
    return window.matchMedia(FIXED_SIDEBAR_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

const FixedSidebarContext = createContext({
  enabled: false,
  available: false,
  setEnabled: () => {},
  toggle: () => {},
});

export function FixedSidebarProvider({ children, onChange }) {
  const [stored, setStored] = useState(readFixedSidebarPreference);
  // getServerSnapshot returns false: with no window to measure, assume the
  // screen is too narrow and render the plain layout.
  const wideEnough = useSyncExternalStore(
    subscribeToWidth,
    readWideEnough,
    () => false
  );

  // `available` gates the controls that offer the mode; `enabled` is what the
  // layout acts on. They part company on a narrow screen with the preference
  // stored on: the option is hidden and the sidebar stays down, but the choice
  // is remembered and comes back with the width.
  const enabled = stored && wideEnough;

  const setEnabled = useCallback(
    (next) => {
      setStored(next);
      writeFixedSidebarPreference(next);
      // The legacy chrome reloads in onChange: its navbar, sidebar and footer
      // are separate React roots that cannot re-render off shared context.
      onChange?.(next);
    },
    [onChange]
  );

  const toggle = useCallback(() => setEnabled(!enabled), [enabled, setEnabled]);

  const value = useMemo(
    () => ({ enabled, available: wideEnough, setEnabled, toggle }),
    [enabled, wideEnough, setEnabled, toggle]
  );

  return (
    <FixedSidebarContext.Provider value={value}>
      {children}
    </FixedSidebarContext.Provider>
  );
}

export function useFixedSidebar() {
  return useContext(FixedSidebarContext);
}
